import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Row, Col, Card, Table, Typography, Spin, Tag, Tooltip, Button, Space, Empty } from 'antd';
import { ReloadOutlined, DashboardOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { getServerStats, getNetConfigStats, getBandwidthHistory } from '../api/dashboard';
import { getApiLogs, ApiLog } from '../api/api-log';
import { ServerStats, NetConfigStats, BandwidthPoint } from '../types/api';
import { handleApiError } from '../utils/errorHandler';
import { TimeUtil } from '../utils/timeUtil';

const { Title, Text } = Typography;

const ONLINE_COLOR = '#22c55e';
const OFFLINE_COLOR = '#ef4444';
const RX_COLOR = '#22d3ee';
const TX_COLOR = '#a855f7';

// bytes/s → 人类可读速率（兼容 echarts 回调传入字符串的情况）
const formatRate = (input: unknown): string => {
  const bytesPerSec = typeof input === 'number' ? input : parseFloat(String(input));
  if (!isFinite(bytesPerSec) || bytesPerSec <= 0) return '0 B/s';
  const units = ['B/s', 'KB/s', 'MB/s', 'GB/s'];
  let v = bytesPerSec;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
};

// 环形饼图配置（中心显示总量）
const buildPieOption = (online: number, offline: number, total: number, unit: string): EChartsOption => ({
  tooltip: {
    trigger: 'item',
    formatter: (p: any) => `${p.name}: ${p.value} ${unit} (${p.percent}%)`,
  },
  series: [
    {
      type: 'pie',
      radius: ['58%', '80%'],
      center: ['50%', '50%'],
      avoidLabelOverlap: true,
      itemStyle: { borderColor: '#fff', borderWidth: 2 },
      label: { show: false },
      emphasis: {
        label: { show: false },
        scaleSize: 6,
      },
      data: [
        { name: '在线', value: online, itemStyle: { color: ONLINE_COLOR } },
        { name: '离线', value: offline, itemStyle: { color: OFFLINE_COLOR } },
      ],
    },
  ],
  graphic: [
    {
      type: 'text',
      left: 'center',
      top: '42%',
      style: { text: String(total), fontSize: 28, fontWeight: 700, fill: '#1f2937' },
    },
    {
      type: 'text',
      left: 'center',
      top: '58%',
      style: { text: `${unit}总量`, fontSize: 12, fill: '#9ca3af' },
    },
  ],
});

// 带宽曲线配置
const buildBandwidthOption = (points: BandwidthPoint[]): EChartsOption => ({
  tooltip: {
    trigger: 'axis',
    formatter: (params: any) => {
      const list = Array.isArray(params) ? params : [params];
      if (!list.length) return '';
      const idx = list[0].dataIndex;
      const p = points[idx];
      const time = p ? TimeUtil.formatServerTime(p.sampleTime, 'MM-DD HH:mm') : list[0].axisValueLabel;
      const online = p ? `<br/>在线服务器: <b>${p.onlineServers}</b> 台` : '';
      return (
        `<b>${time}</b>` +
        list
          .map((s: any) => `<br/>${s.marker}${s.seriesName}: <b>${formatRate(s.value)}</b>`)
          .join('') +
        online
      );
    },
  },
  legend: { data: ['下行 (RX)', '上行 (TX)'], top: 0 },
  grid: { left: 60, right: 24, top: 40, bottom: 64 },
  xAxis: {
    type: 'time',
    axisLabel: { formatter: '{HH}:{mm}', color: '#6b7280' },
    splitLine: { show: false },
  },
  yAxis: {
    type: 'value',
    name: 'bytes/s',
    axisLabel: { formatter: (v: number) => formatRate(v), color: '#6b7280' },
    splitLine: { lineStyle: { type: 'dashed', color: '#e5e7eb' } },
  },
  dataZoom: [
    { type: 'inside', start: 0, end: 100 },
    { type: 'slider', height: 20, bottom: 8, start: 0, end: 100 },
  ],
  series: [
    {
      name: '下行 (RX)',
      type: 'line',
      smooth: true,
      showSymbol: false,
      lineStyle: { width: 2, color: RX_COLOR },
      itemStyle: { color: RX_COLOR },
      areaStyle: {
        color: {
          type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(34, 211, 238, 0.28)' },
            { offset: 1, color: 'rgba(34, 211, 238, 0.02)' },
          ],
        } as any,
      },
      data: points.map((p) => [p.sampleTime, Number(p.totalRxRate) || 0]),
    },
    {
      name: '上行 (TX)',
      type: 'line',
      smooth: true,
      showSymbol: false,
      lineStyle: { width: 2, color: TX_COLOR },
      itemStyle: { color: TX_COLOR },
      areaStyle: {
        color: {
          type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(168, 85, 247, 0.25)' },
            { offset: 1, color: 'rgba(168, 85, 247, 0.02)' },
          ],
        } as any,
      },
      data: points.map((p) => [p.sampleTime, Number(p.totalTxRate) || 0]),
    },
  ],
});

// 饼图下方图例说明
const PieLegend: React.FC<{ online: number; offline: number; unit: string }> = ({ online, offline, unit }) => (
  <Space size={24} style={{ justifyContent: 'center', width: '100%', marginTop: 4 }}>
    <span>
      <Tag color="success" style={{ marginRight: 4 }}>在线</Tag>
      <Text strong>{online}</Text> <Text type="secondary">{unit}</Text>
    </span>
    <span>
      <Tag color="error" style={{ marginRight: 4 }}>离线</Tag>
      <Text strong>{offline}</Text> <Text type="secondary">{unit}</Text>
    </span>
  </Space>
);

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<ServerStats>({ totalServers: 0, onlineServers: 0, offlineServers: 0 });
  const [netStats, setNetStats] = useState<NetConfigStats>({
    ppp: { total: 0, online: 0, offline: 0 },
    staticIp: { total: 0, online: 0, offline: 0 },
  });
  const [bandwidth, setBandwidth] = useState<BandwidthPoint[]>([]);
  const [recentApiLogs, setRecentApiLogs] = useState<ApiLog[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchData = useCallback(async (showSpin = false) => {
    if (showSpin) setLoading(true);
    // 各数据块独立容错：任一失败不拖垮整个仪表盘
    const [statsR, netR, bwR, logsR] = await Promise.allSettled([
      getServerStats(),
      getNetConfigStats(),
      getBandwidthHistory(24),
      getApiLogs({ page: 1, limit: 10, skipTotal: true }, { timeout: 60000 }),
    ]);
    if (statsR.status === 'fulfilled') setStats(statsR.value.data);
    else handleApiError(statsR.reason, '获取服务器统计失败');
    if (netR.status === 'fulfilled') setNetStats(netR.value.data);
    else handleApiError(netR.reason, '获取网络配置统计失败');
    if (bwR.status === 'fulfilled') setBandwidth(bwR.value.data);
    else handleApiError(bwR.reason, '获取带宽历史失败');
    if (logsR.status === 'fulfilled') setRecentApiLogs(logsR.value.data.data);
    setLastUpdate(new Date());
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData(true);
    // 每 60s 自动刷新（与后端带宽采样周期一致）
    timerRef.current = setInterval(() => fetchData(false), 60000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [fetchData]);

  // 状态码颜色映射
  const getStatusColor = (status: number) => {
    if (status >= 200 && status < 300) return 'success';
    if (status >= 300 && status < 400) return 'warning';
    if (status >= 400 && status < 500) return 'error';
    if (status >= 500) return 'error';
    return 'default';
  };

  // 方法颜色映射
  const getMethodColor = (method: string) => {
    const colors: { [key: string]: string } = {
      GET: 'blue', POST: 'green', PUT: 'orange', DELETE: 'red', PATCH: 'purple',
    };
    return colors[method] || 'default';
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: '用户', dataIndex: 'user', key: 'user', width: 100, render: (t: string | null) => t || '-' },
    {
      title: '方法', dataIndex: 'method', key: 'method', width: 80,
      render: (method: string) => <Tag color={getMethodColor(method)}>{method}</Tag>,
    },
    {
      title: 'URL', dataIndex: 'url', key: 'url', ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text}><span style={{ fontFamily: 'monospace' }}>{text}</span></Tooltip>
      ),
    },
    {
      title: '状态码', dataIndex: 'status', key: 'status', width: 100,
      render: (status: number) => <Tag color={getStatusColor(status)}>{status}</Tag>,
    },
    {
      title: '时间', dataIndex: 'createTime', key: 'createTime', width: 180,
      render: (text: string) => TimeUtil.formatServerTime(text, 'MM-DD HH:mm:ss'),
    },
  ];

  const latest = bandwidth.length ? bandwidth[bandwidth.length - 1] : null;

  return (
    <Spin spinning={loading}>
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={2} style={{ margin: 0 }}>系统概览</Title>
          <Space>
            {lastUpdate && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                更新于 {TimeUtil.formatServerTime(lastUpdate.toISOString(), 'HH:mm:ss')} · 每 60s 自动刷新
              </Text>
            )}
            <Button icon={<ReloadOutlined />} onClick={() => fetchData(true)}>刷新</Button>
          </Space>
        </div>

        {/* 三饼图：服务器 / PPP拨号 / 静态IP */}
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Card title="服务器总量（在线/离线）" size="small">
              <ReactECharts
                option={buildPieOption(stats.onlineServers, stats.offlineServers, stats.totalServers, '台')}
                style={{ height: 240 }}
                notMerge
              />
              <PieLegend online={stats.onlineServers} offline={stats.offlineServers} unit="台" />
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card title="总拨号数量（在线/离线）" size="small">
              <ReactECharts
                option={buildPieOption(netStats.ppp.online, netStats.ppp.offline, netStats.ppp.total, '路')}
                style={{ height: 240 }}
                notMerge
              />
              <PieLegend online={netStats.ppp.online} offline={netStats.ppp.offline} unit="路" />
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card title="总静态IP数量（在线/离线）" size="small">
              <ReactECharts
                option={buildPieOption(netStats.staticIp.online, netStats.staticIp.offline, netStats.staticIp.total, '个')}
                style={{ height: 240 }}
                notMerge
              />
              <PieLegend online={netStats.staticIp.online} offline={netStats.staticIp.offline} unit="个" />
            </Card>
          </Col>
        </Row>

        {/* 24h 带宽曲线 */}
        <Card
          style={{ marginTop: 16 }}
          size="small"
          title={
            <Space>
              <DashboardOutlined />
              <span>在线服务器实时总带宽（最近 24 小时，每分钟采样）</span>
            </Space>
          }
          extra={
            latest ? (
              <Space size={16}>
                <Text style={{ color: RX_COLOR }}>
                  当前下行 <Text strong>{formatRate(latest.totalRxRate)}</Text>
                </Text>
                <Text style={{ color: TX_COLOR }}>
                  当前上行 <Text strong>{formatRate(latest.totalTxRate)}</Text>
                </Text>
                <Text type="secondary">在线 {latest.onlineServers} 台</Text>
              </Space>
            ) : null
          }
        >
          {bandwidth.length > 0 ? (
            <ReactECharts option={buildBandwidthOption(bandwidth)} style={{ height: 340 }} notMerge />
          ) : (
            <Empty
              style={{ padding: '60px 0' }}
              description="暂无带宽采样数据（后端每分钟采样一次，稍候刷新即可看到曲线）"
            />
          )}
        </Card>

        <div style={{ marginTop: 24 }}>
          <Title level={3}>最近API提交日志</Title>
          <Table
            columns={columns}
            dataSource={recentApiLogs}
            rowKey="id"
            pagination={false}
            size="small"
          />
        </div>
      </div>
    </Spin>
  );
};

export default Dashboard;
